// Map links read and written without asking the server anything (the same query params
// App.js reads), for the map's gate (MapGate.js) and the offers' links back to the map,
// a home or a report after subscribing or logging in.
import { SUBSCRIBE_PATH, SUB_PLANS } from "./accountRules";
import { propertyBackPath } from "../property/propertyValue";

// The UK bounds App.js accepts a point in, in Web Mercator (EPSG:3857)
const R = 6378137;
const mercX = (lon) => R * lon * Math.PI / 180;
const mercY = (lat) => R * Math.log(Math.tan(Math.PI / 4 + lat * Math.PI / 360));
const UK = {minX:mercX(-8.948190346097487), maxX:mercX(2.267968716721079), minY:mercY(49.823809), maxY:mercY(59.58730581272295)};

export const DEFAULT_DIST = 800;

// {point, d, layers, name, back}: the point (null when the link has none, or it is
// outside the UK), the distance to show around it, the layers it asked for, the
// name to show for the place, and the property page to go back to (or null)
export const paywallView = (search) => {
    const params = new URLSearchParams(search);
    const x = parseFloat(params.get('x'));
    const y = parseFloat(params.get('y'));
    const inUk = Number.isFinite(x) && Number.isFinite(y) && x >= UK.minX && x <= UK.maxX && y >= UK.minY && y <= UK.maxY;
    const d = parseInt(params.get('d'), 10);
    const name = (params.get('n') || '').trim().slice(0, 120);
    return {
        point:inUk ? {x, y} : null,
        d:Number.isFinite(d) ? Math.min(3300, Math.max(400, d)) : DEFAULT_DIST,
        layers:(params.get('s') || '').split(',').filter(Boolean),
        name:name || null,
        back:propertyBackPath(search),
    };
};

// A map page to come back to after subscribing or logging in: the map itself, or a
// '/' share link carrying its state. Anything else is refused, so the param can't
// send anyone off the site.
export const safeMapReturn = (next) => {
    if (typeof next !== 'string' || next.startsWith('//')) return null;
    return next === '/map' || next.startsWith('/map?') || next.startsWith('/?') ? next : null;
};

// A home's page to come back to after subscribing, from its area offer (PropertyLookup.js)
const PROPERTY_RETURN = /^\/property\/[A-Za-z0-9-]+\/[0-9]+$/;
export const isPropertyReturn = (next) => typeof next === 'string' && PROPERTY_RETURN.test(next);

// A report page to come back to after subscribing, from its locked page: the listing
// lookup (/listing, /listing?..., /listing/<id>) or an area report (/area-report?...)
const REPORT_RETURN = /^\/(listing(\/[0-9]+)?|area-report)(\?.*)?$/;
export const isReportReturn = (next) => typeof next === 'string' && !next.startsWith('//') && REPORT_RETURN.test(next);

// Where the Subscribe page may send someone back to: a map page, a home's page or a report
export const safeSubscribeReturn = (next) => safeMapReturn(next) || (isPropertyReturn(next) || isReportReturn(next) ? next : null);

// The words of the Subscribe page's button back to where they came from
export const returnLabel = (next) => isPropertyReturn(next) ? 'Back to the home'
    : isReportReturn(next) ? (next.startsWith('/area-report') ? 'Open the area report' : 'Carry on with the report') : 'Open the full map';

// The Subscribe page with a plan chosen and the page to come back to
export const subscribeLink = (planId, next) => {
    const params = new URLSearchParams();
    if (SUB_PLANS.some((p) => p.id === planId)) params.set('plan', planId);
    if (safeSubscribeReturn(next)) params.set('next', next);
    const q = params.toString();
    return SUBSCRIBE_PATH + (q ? '?' + q : '');
};

// The Big Picture's maps (the landing page's Big Picture section): the whole-country maps
// (areaMapLayers in Layers/layerBasics.js, kept in step by hand to keep the map code out of
// here) and the biggest price movers list. A guest can switch between them freely (mapAccessRules.js).
export const OPEN_MAP_LAYERS = ['propertyLad', 'crimeLad', 'deprivationLad', 'rentLad', 'salesRateLad', 'migrationLad', 'imdLad', 'priceChangeList'];

// The query of a map link for the map as it is now, to come back to after logging in or subscribing
export const viewSearch = (layers, point, dist, name) => {
    const params = new URLSearchParams();
    if (point && point.x && point.y) {
        params.set('x', String(Math.round(point.x)));
        params.set('y', String(Math.round(point.y)));
        if (dist) params.set('d', String(dist));
        if (name) params.set('n', name);
    }
    if (layers.length) params.set('s', layers.join(','));
    return '?' + params.toString();
};
